import React, { useMemo, useState, useEffect, useRef } from 'react';
import './onboarding.css';
import pb from '../lib/pocketbase';

function StepIndicator({ step, total }) {
  return (
    <div className="wizard-steps">
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} className={`wizard-step ${step === i + 1 ? 'active' : step > i + 1 ? 'done' : ''}`}>
          {i + 1}
        </div>
      ))}
    </div>
  );
}

export default function OnboardingWizard() {
  const total = 5;
  const [step, setStep] = useState(1);
  const [status, setStatus] = useState('particulier'); // 'particulier' | 'professionnel'
  const [client, setClient] = useState({ name: '', company: '', email: '', phone: '', address: '', postalCode: '', city: '', password: '', confirm: '' });
  const [pro, setPro] = useState({ siret: '', tva: '', rcs: '' });
  const [vehicles, setVehicles] = useState([{ brand: '', model: '', year: '', license_plate: '', mileage: '' }]);
  const [notes, setNotes] = useState('');
  const [draftCustomerId, setDraftCustomerId] = useState(() => localStorage.getItem('onb_customer_id') || '');
  const savingRef = useRef(false);
  const [showPwError, setShowPwError] = useState(false);
  const [clientErrors, setClientErrors] = useState({});
  const [vehicleErrors, setVehicleErrors] = useState([]);
  const canPrev = step > 1;
  
  // Validate any stored draft on mount; clear if missing
  useEffect(() => {
    const validateDraft = async () => {
      const id = localStorage.getItem('onb_customer_id');
      if (!id) return;
      try {
        await pb.collection('customers').getOne(id);
      } catch (e) {
        localStorage.removeItem('onb_customer_id');
        setDraftCustomerId('');
      }
    };
    validateDraft();
  }, []);

  // Validation minimale par étape
  const stepValid = useMemo(() => {
    if (step === 1) return status === 'particulier' || status === 'professionnel';
    if (step === 2) {
      const emailOk = !!client.email && /.+@.+\..+/.test(client.email);
      const nameOk = !!client.name;
      const pwOk = (client.password || '').length >= 8;
      const confirmOk = client.password === client.confirm;
      return emailOk && nameOk && pwOk && confirmOk;
    }
    if (step === 3) return vehicles.length >= 1; // champs libres, on laisse minimal
    return true;
  }, [step, status, client, vehicles]);
  // Allow clicking "Suivant" on step 2 even if invalid, to show inline feedback only on attempt
  const canNext = step < total && (step === 2 ? true : stepValid);

  const addVehicle = () => setVehicles(vs => [...vs, { brand: '', model: '', year: '', license_plate: '', mileage: '' }]);
  const removeVehicle = (idx) => setVehicles(vs => vs.filter((_, i) => i !== idx));

  const next = async () => {
    if (step === 2) {
      const emailOk = !!client.email && /.+@.+\..+/.test(client.email);
      const nameOk = !!client.name;
      const pwOk = (client.password || '').length >= 8;
      const confirmOk = client.password === client.confirm;
      const phoneOk = !!client.phone;
      const addressOk = !!client.address;
      const postalOk = !!client.postalCode;
      const cityOk = !!client.city;

      const errors = {};
      if (!nameOk) errors.name = 'Veuillez renseigner votre nom et prénom.';
      if (!emailOk) errors.email = 'Veuillez renseigner un email valide.';
      if (!pwOk) errors.password = 'Le mot de passe doit contenir au moins 8 caractères.';
      if (!confirmOk) errors.confirm = 'Les mots de passe ne correspondent pas.';
      if (!phoneOk) errors.phone = 'Veuillez renseigner votre numéro de téléphone.';
      if (!addressOk) errors.address = 'Veuillez renseigner votre adresse.';
      if (!postalOk) errors.postalCode = 'Veuillez renseigner votre code postal.';
      if (!cityOk) errors.city = 'Veuillez renseigner votre ville.';

      if (!(emailOk && nameOk && pwOk && confirmOk && phoneOk && addressOk && postalOk && cityOk)) {
        setClientErrors(errors);
        setShowPwError(!confirmOk);
        return;
      }

      setClientErrors({});
      setShowPwError(false);

      // Création du client uniquement après validation complète de l'étape 2
      try {
        await createCustomerAfterStep2();
      } catch (e) {
        console.error('Erreur lors de la création du client après étape 2', e);
        alert("Impossible de créer votre fiche client pour le moment. Veuillez réessayer plus tard.");
        return;
      }
    } else if (step === 3) {
      // Validation des véhicules : marque, modèle et immatriculation obligatoires
      const errorsArray = vehicles.map(v => {
        const ve = {};
        if (!v.brand) ve.brand = 'Veuillez renseigner la marque.';
        if (!v.model) ve.model = 'Veuillez renseigner le modèle.';
        if (!v.license_plate) ve.license_plate = "Veuillez renseigner l'immatriculation.";
        return ve;
      });

      const hasErrors = errorsArray.some(e => Object.keys(e).length > 0);
      if (hasErrors) {
        setVehicleErrors(errorsArray);
        return; // on ne passe pas à l’étape suivante
      }
      setVehicleErrors([]);
    }
    setStep(s => Math.min(total, s + 1));
  };
  const prev = () => setStep(s => Math.max(1, s - 1));

  const summary = useMemo(() => ({ status, client, pro, vehicles, notes }), [status, client, pro, vehicles, notes]);

  const saveCustomerDraft = async () => {
    // Ne sauvegarde que si un brouillon existe déjà : 
    // la création initiale est faite après validation de l'étape 2.
    if (savingRef.current || !draftCustomerId) return;
    const payload = {
      name: client.name || 'Client',
      email: client.email || '',
      phone: client.phone || '',
      address: [client.address, client.postalCode, client.city].filter(Boolean).join(' '),
      company: status === 'professionnel' ? (client.company || '') : '',
      notes: notes || ''
    };
    try {
      savingRef.current = true;
      try {
        await pb.collection('customers').update(draftCustomerId, payload);
      } catch (e) {
        // Si le brouillon a été supprimé côté serveur (404) :
        // - Avant validation de l'étape 2, on NE recrée PAS automatiquement pour éviter
        //   des fiches clients non désirées.
        // - À partir de l'étape 2, on recrée une fiche proprement avec les infos saisies.
        if (e?.status === 404) {
          if (step < 2) {
            // On nettoie simplement la référence locale et on attend la vraie création
            // lors de la validation de l'étape 2.
            localStorage.removeItem('onb_customer_id');
            setDraftCustomerId('');
          } else {
            const rec = await pb.collection('customers').create(payload);
            setDraftCustomerId(rec.id);
            localStorage.setItem('onb_customer_id', rec.id);
          }
        } else {
          throw e;
        }
      }
    } catch (e) {
      console.error('Draft customer save error', e);
    } finally {
      savingRef.current = false;
    }
  };

  const createCustomerAfterStep2 = async () => {
    if (draftCustomerId || savingRef.current) return;
    const payload = {
      name: client.name,
      email: client.email,
      phone: client.phone || '',
      address: [client.address, client.postalCode, client.city].filter(Boolean).join(' '),
      company: status === 'professionnel' ? (client.company || '') : '',
      notes: notes || ''
    };
    try {
      savingRef.current = true;
      const rec = await pb.collection('customers').create(payload);
      setDraftCustomerId(rec.id);
      localStorage.setItem('onb_customer_id', rec.id);
    } catch (e) {
      console.error('Initial customer create error (step 2 validation)', e);
      throw e;
    } finally {
      savingRef.current = false;
    }
  };

  const saveVehiclesDraft = async () => {
    if (!draftCustomerId) return;
    try {
      const updated = await Promise.all(vehicles.map(async (v) => {
        const data = {
          customer_id: draftCustomerId,
          brand: v.brand || '',
          model: v.model || '',
          year: v.year ? Number(v.year) : undefined,
          license_plate: v.license_plate || '',
          mileage: v.mileage ? Number(String(v.mileage).replace(/\s+/g,'').replace(/[^\d]/g,'')) : undefined
        };
        if (v.id) {
          await pb.collection('vehicles').update(v.id, data);
          return v;
        } else {
          const rec = await pb.collection('vehicles').create(data);
          return { ...v, id: rec.id };
        }
      }));
      setVehicles(updated);
    } catch (e) {
      console.error('Draft vehicles save error', e);
    }
  };

  useEffect(() => {
    if (step === 1 || step === 2) {
      const t = setTimeout(saveCustomerDraft, 500);
      return () => clearTimeout(t);
    }
  }, [status, client.name, client.company, client.email, client.phone, client.address, client.postalCode, client.city, step]);

  useEffect(() => {
    if (step === 4) {
      const t = setTimeout(saveCustomerDraft, 500);
      return () => clearTimeout(t);
    }
  }, [notes, step]);

  useEffect(() => {
    if (step === 3) {
      const t = setTimeout(saveVehiclesDraft, 700);
      return () => clearTimeout(t);
    }
  }, [vehicles, step]);

  const handleSubmit = async () => {
    try {
      await saveCustomerDraft();
      // Ensure we have a valid draft id before vehicles
      if (!draftCustomerId) {
        // refresh from storage if recreated
        const id = localStorage.getItem('onb_customer_id');
        if (id) setDraftCustomerId(id);
      }
      if (vehicles.length && draftCustomerId) await saveVehiclesDraft();
      const email = client.email;
      const password = client.password;
      const passwordConfirm = client.confirm;
      const name = client.name;
      try {
        await pb.collection('users').create({ email, password, passwordConfirm, name, emailVisibility: true });
      } catch (e) {
        // If email already exists, try login instead
        if (e?.status === 400) {
          try {
            await pb.collection('users').authWithPassword(email, password);
          } catch (loginErr) {
            throw e; // rethrow original create error if login fails
          }
        } else {
          throw e;
        }
      }
      // If not already authenticated, login now
      if (!pb.authStore?.isValid) {
        await pb.collection('users').authWithPassword(email, password);
      }
      if (draftCustomerId && pb.authStore?.model?.id) {
        await pb.collection('customers').update(draftCustomerId, { created_by: pb.authStore.model.id });
      }
      // Clear draft after success
      localStorage.removeItem('onb_customer_id');
      window.location.assign('/mon-compte');
    } catch (e) {
      console.error('Final submit error', e);
      const msg = e?.data?.message || e?.message || 'Une erreur est survenue lors de la validation.';
      alert(msg);
    }
  };

  return (
    <div className="onboarding-page">
      <div className="container">
        <div className="onboarding-header">
          <div className="brand">
            <img src="/images/logo.jpeg" alt="Christophe Dépanne" className="brand-logo" />
            <span className="brand-name">Christophe Dépanne</span>
          </div>
          <div className="head-right">
            <span className="head-title">Ouverture de compte</span>
            <a className="back-link" href="/" aria-label="Retour au site">Retour au site</a>
          </div>
        </div>
        <div className="welcome-logo-wrap">
          <img src="/images/logo.jpeg" alt="Christophe Dépanne" className="welcome-logo" />
        </div>
        <div className="onboarding-welcome">Bienvenue chez Christophe Dépanne !</div>
        <div className="onboarding-intro">
          Ce parcours vous permet de créer votre compte client en quelques étapes. Vous pourrez saisir vos informations, ajouter votre/vos véhicule(s), puis accéder à votre espace client.
        </div>
        <StepIndicator step={step} total={total} />

        {step === 1 && (
          <div className="card">
            <h2 style={{ textAlign: 'center' }}>Vous êtes</h2>
            <div className="status-cards">
              <button
                type="button"
                className={`status-card ${status==='particulier' ? 'active' : ''}`}
                onClick={() => setStatus('particulier')}
              >
                <div className="status-icon">
                  <svg width="42" height="42" viewBox="0 0 24 24" fill="currentColor"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
                </div>
                <div className="status-title">Particulier</div>
              </button>
              <button
                type="button"
                className={`status-card ${status==='professionnel' ? 'active' : ''}`}
                onClick={() => setStatus('professionnel')}
              >
                <div className="status-icon">
                  <svg width="42" height="42" viewBox="0 0 24 24" fill="currentColor"><path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/></svg>
                </div>
                <div className="status-title">Professionnel</div>
              </button>
            </div>
            {/* Statut uniquement, aucun champ pro à cette étape */}
          </div>
        )}

        {step === 2 && (
          <div className="card">
            <h2>Vos informations</h2>

            {Object.keys(clientErrors).length > 0 && (
              <div className="step-error">
                Merci de compléter correctement tous les champs avant de continuer.
              </div>
            )}

            <div className="grid form-grid">
              <div>
                <input
                  className={clientErrors.name ? 'input-error' : ''}
                  placeholder="Nom et prénom"
                  value={client.name}
                  onChange={e=>setClient({ ...client, name: e.target.value })}
                />
                {clientErrors.name && <div className="field-error">{clientErrors.name}</div>}
              </div>

              {status==='professionnel' && (
                <div>
                  <input
                    placeholder="Société"
                    value={client.company}
                    onChange={e=>setClient({ ...client, company: e.target.value })}
                  />
                </div>
              )}

              <div>
                <input
                  className={clientErrors.email ? 'input-error' : ''}
                  placeholder="Email"
                  type="email"
                  value={client.email}
                  onChange={e=>setClient({ ...client, email: e.target.value })}
                />
                {clientErrors.email && <div className="field-error">{clientErrors.email}</div>}
              </div>

              <div>
                <input
                  className={clientErrors.phone ? 'input-error' : ''}
                  placeholder="Téléphone"
                  value={client.phone}
                  onChange={e=>setClient({ ...client, phone: e.target.value })}
                />
                {clientErrors.phone && <div className="field-error">{clientErrors.phone}</div>}
              </div>

              <div>
                <input
                  className={clientErrors.address ? 'input-error' : ''}
                  placeholder="Adresse"
                  value={client.address}
                  onChange={e=>setClient({ ...client, address: e.target.value })}
                />
                {clientErrors.address && <div className="field-error">{clientErrors.address}</div>}
              </div>

              <div>
                <input
                  className={clientErrors.postalCode ? 'input-error' : ''}
                  placeholder="Code postal"
                  value={client.postalCode}
                  onChange={e=>setClient({ ...client, postalCode: e.target.value })}
                />
                {clientErrors.postalCode && <div className="field-error">{clientErrors.postalCode}</div>}
              </div>

              <div>
                <input
                  className={clientErrors.city ? 'input-error' : ''}
                  placeholder="Ville"
                  value={client.city}
                  onChange={e=>setClient({ ...client, city: e.target.value })}
                />
                {clientErrors.city && <div className="field-error">{clientErrors.city}</div>}
              </div>

              <div>
                <input
                  className={clientErrors.password ? 'input-error' : ''}
                  placeholder="Mot de passe (min. 8 caractères)"
                  type="password"
                  value={client.password}
                  onChange={e=>setClient({ ...client, password: e.target.value })}
                />
                {clientErrors.password && <div className="field-error">{clientErrors.password}</div>}
              </div>

              <div>
                <input
                  className={clientErrors.confirm ? 'input-error' : ''}
                  placeholder="Confirmer le mot de passe"
                  type="password"
                  value={client.confirm}
                  onChange={e=>setClient({ ...client, confirm: e.target.value })}
                />
                {clientErrors.confirm && <div className="field-error">{clientErrors.confirm}</div>}
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="card">
            <h2>Votre(vos) véhicule(s)</h2>

            {vehicleErrors.some(e => Object.keys(e).length > 0) && (
              <div className="step-error">
                Merci de compléter les informations obligatoires de votre(vos) véhicule(s).
              </div>
            )}

            {vehicles.map((v, idx) => (
              <div key={idx} className="vehicle-block">
                <div className="grid">
                  <div>
                    <input
                      className={vehicleErrors[idx]?.brand ? 'input-error' : ''}
                      placeholder="Marque"
                      value={v.brand}
                      onChange={e=>{
                        const c=[...vehicles]; c[idx]={...c[idx], brand:e.target.value}; setVehicles(c);
                      }}
                    />
                    {vehicleErrors[idx]?.brand && (
                      <div className="field-error">{vehicleErrors[idx].brand}</div>
                    )}
                  </div>

                  <div>
                    <input
                      className={vehicleErrors[idx]?.model ? 'input-error' : ''}
                      placeholder="Modèle"
                      value={v.model}
                      onChange={e=>{
                        const c=[...vehicles]; c[idx]={...c[idx], model:e.target.value}; setVehicles(c);
                      }}
                    />
                    {vehicleErrors[idx]?.model && (
                      <div className="field-error">{vehicleErrors[idx].model}</div>
                    )}
                  </div>

                  <div>
                    <input
                      placeholder="Année"
                      value={v.year}
                      onChange={e=>{
                        const c=[...vehicles]; c[idx]={...c[idx], year:e.target.value}; setVehicles(c);
                      }}
                    />
                  </div>

                  <div>
                    <input
                      className={vehicleErrors[idx]?.license_plate ? 'input-error' : ''}
                      placeholder="Immatriculation"
                      value={v.license_plate}
                      onChange={e=>{
                        const c=[...vehicles]; c[idx]={...c[idx], license_plate:e.target.value}; setVehicles(c);
                      }}
                    />
                    {vehicleErrors[idx]?.license_plate && (
                      <div className="field-error">{vehicleErrors[idx].license_plate}</div>
                    )}
                  </div>

                  <div>
                    <input
                      placeholder="Kilométrage"
                      value={v.mileage}
                      onChange={e=>{
                        const c=[...vehicles]; c[idx]={...c[idx], mileage:e.target.value}; setVehicles(c);
                      }}
                    />
                  </div>
                </div>
                {vehicles.length>1 && (
                  <button className="btn danger" onClick={()=>removeVehicle(idx)}>Supprimer</button>
                )}
                <hr />
              </div>
            ))}
            <button className="btn" onClick={addVehicle}>Ajouter un véhicule</button>
          </div>
        )}

        {step === 4 && (
          <div className="card">
            <h2>Votre demande (optionnel)</h2>
            <p style={{ margin: '4px 0 8px', color: '#6b7280' }}>
              Expliquez-nous votre besoin: symptômes, contexte, disponibilité, urgences, préférences de contact…
            </p>
            <textarea
              rows={6}
              placeholder="Décrivez votre demande (ex: bruit à l'avant droit, vidange à prévoir, dispo mardi matin)…"
              value={notes}
              onChange={e=>setNotes(e.target.value)}
            />
          </div>
        )}

        {step === 5 && (
          <div className="card">
            <h2>Récapitulatif</h2>
            <div className="summary">
              <div className="summary-section">
                <div className="summary-title">Statut</div>
                <div className="summary-row"><span>Type</span><strong>{status === 'professionnel' ? 'Professionnel' : 'Particulier'}</strong></div>
              </div>

              <div className="summary-section">
                <div className="summary-title">Client</div>
                <div className="summary-row"><span>Nom</span><strong>{client.name || '-'}</strong></div>
                {status === 'professionnel' && (
                  <div className="summary-row"><span>Société</span><strong>{client.company || '-'}</strong></div>
                )}
                <div className="summary-row"><span>Email</span><strong>{client.email || '-'}</strong></div>
                <div className="summary-row"><span>Téléphone</span><strong>{client.phone || '-'}</strong></div>
                <div className="summary-row"><span>Adresse</span><strong>{[client.address, client.postalCode, client.city].filter(Boolean).join(' ') || '-'}</strong></div>
                <div className="summary-row"><span>Mot de passe</span><strong>{client.password ? '•'.repeat(Math.min(client.password.length, 12)) : '-'}</strong></div>
              </div>

              {/* Pas de section SIRET/TVA/RCS dans le récap */}

              <div className="summary-section">
                <div className="summary-title">Véhicule(s)</div>
                {vehicles.length === 0 && (
                  <div className="summary-empty">Aucun véhicule</div>
                )}
                {vehicles.map((v, i) => {
                  const km = v.mileage ? String(v.mileage).replace(/\B(?=(\d{3})+(?!\d))/g, ' ') : '';
                  return (
                    <div key={i} className="summary-vehicle">
                      <div className="summary-row"><span>Modèle</span><strong>{[v.brand, v.model, v.year ? `(${v.year})` : ''].filter(Boolean).join(' ') || '-'}</strong></div>
                      <div className="summary-row"><span>Immatriculation</span><strong>{v.license_plate || '-'}</strong></div>
                      <div className="summary-row"><span>Kilométrage</span><strong>{km ? `${km} km` : '-'}</strong></div>
                    </div>
                  );
                })}
              </div>

              <div className="summary-section">
                <div className="summary-title">Notes</div>
                <div className="summary-notes">{notes || <span className="summary-empty">Aucune</span>}</div>
              </div>
            </div>
            <p style={{ marginTop: 8 }}>Validation créera votre compte, enregistrera vos infos et véhicules, puis vous connectera.</p>
          </div>
        )}

        <div className="wizard-actions">
          <button className="btn" disabled={!canPrev} onClick={prev}>Précédent</button>
          {step < total ? (
            <button className="btn primary" disabled={!canNext} onClick={next}>Suivant</button>
          ) : (
            <button className="btn success" onClick={handleSubmit}>Valider</button>
          )}
        </div>
      </div>
      <div className="onboarding-footer">
        <div className="container">
          <div className="footer-inner">© {new Date().getFullYear()} Christophe Dépanne — Onboarding client</div>
        </div>
      </div>
    </div>
  );
}
